import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import patientFallbackImage from "assets/images/patient-fallback.svg";
import {
  unlinkAnimalFromContact,
  mapRawAnimalsToPatientRows,
  PatientRow,
} from "services/patients.service";
import { readContactCompleteByContactId } from "services/contacts.service";
import ConfirmDialog from "components/ConfirmDialog";
import vgToast from "utils/toast";

const ClientPatientsList = () => {
  const navigate = useNavigate();
  const { id, orgId: orgFromRoute } = useParams();
  const { orgId: activeOrgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const orgId = orgFromRoute ? Number(orgFromRoute) : activeOrgId;
  const contactId = id ? Number(id) : NaN;

  const [patients, setPatients] = React.useState<PatientRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [unlinkTarget, setUnlinkTarget] = React.useState<PatientRow | null>(null);
  const [unlinking, setUnlinking] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0 || !Number.isFinite(contactId) || contactId <= 0) {
      setPatients([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const { data, error: apiError } = await readContactCompleteByContactId(
        accessToken,
        orgId,
        contactId
      );
      if (apiError) {
        setError(apiError.message);
        setPatients([]);
        return;
      }
      setPatients(mapRawAnimalsToPatientRows(data?.animalList ?? []));
    } catch (err) {
      setError((err as Error).message);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId, contactId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const requestUnlink = (patientId: string) => {
    const target = patients.find((p) => p.id === patientId) ?? null;
    if (!target) return;
    setUnlinkTarget(target);
  };

  const handleUnlink = async () => {
    if (!unlinkTarget) return;
    if (orgId <= 0 || !Number.isFinite(contactId) || contactId <= 0) return;
    const animalId = Number(unlinkTarget.id);
    if (!Number.isFinite(animalId) || animalId <= 0) return;

    setUnlinking(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await unlinkAnimalFromContact(
        token,
        orgId,
        contactId,
        animalId
      );
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      vgToast.success("Deceased unlinked.");
      setUnlinkTarget(null);
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setUnlinking(false);
    }
  };

  // Client-side paging: the API returns the whole collection.
  const view = useTableView(patients);

  return (
    <div>
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="m-4"
      />

      <Table<{
        id: string;
        image: string;
        name: string;
        type: string;
      }>
        loading={loading}
        columns={{
          image: "Photo",
          id: "Patient Id",
          name: "Name",
          type: "Species",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        renderCell={(colId, row) => {
          if (colId !== "image") return undefined;
          return (
            <img
              src={row.image || patientFallbackImage}
              alt={row.name || "patient"}
              onError={(e) => {
                const img = e.currentTarget;
                if (img.src !== patientFallbackImage) img.src = patientFallbackImage;
              }}
              className="h-10 w-10 rounded-full object-cover border border-vg-blue/20 bg-vg-blue/[0.04]"
            />
          );
        }}
        handleViewItem={(pid) => navigate(`/app/patients/${pid}`)}
        handleRemoveItem={requestUnlink}
        removeItemLabel="Unlink"
      />

      {loading && (
        <div className="px-4 pb-2 text-sm text-gray-500">Loading patients...</div>
      )}
      {error && <div className="px-4 pb-2 text-sm text-red-500">{error}</div>}

        <PaginationBar
        from={view.from}
        to={view.to}
        total={view.total}
        currentPage={view.page}
        totalPages={view.totalPages}
        onPrev={view.goPrev}
        onNext={view.goNext}
        onPageSelect={view.goToPage}
      />

      <ConfirmDialog
        open={!!unlinkTarget}
        title="Unlink this record?"
        description={
          unlinkTarget
            ? `Remove "${unlinkTarget.name || `Record #${unlinkTarget.id}`}" from this family. The record itself stays intact.`
            : ""
        }
        confirmLabel="Unlink"
        busyLabel="Unlinking…"
        busy={unlinking}
        onCancel={() => {
          if (!unlinking) setUnlinkTarget(null);
        }}
        onConfirm={handleUnlink}
      />
    </div>
  );
};

export default ClientPatientsList;
